/**
 * Local primitives for the verse module.
 *
 * Deliberately self-contained: the hub's ui.tsx and Settings' ui.tsx belong to other
 * modules, and importing across that line couples this feature to files it doesn't own.
 * Everything here is token-only (src/styles.css), >=44px touch targets, real labels,
 * visible focus.
 */
import { useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

type Variant = 'primary' | 'secondary' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-sunk',
  ghost: 'text-ink-soft hover:bg-surface-sunk hover:text-ink',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${VARIANTS[variant]} ${FOCUS} ${className}`}
    />
  );
}

export function IconButton({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...props}
      aria-label={label}
      title={label}
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent ${FOCUS} ${className}`}
    />
  );
}

const CONTROL =
  'min-h-11 w-full rounded-xl border border-border bg-surface px-3 text-base text-ink placeholder:text-ink-faint';

export function TextInput({ className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${FOCUS} ${className}`} />;
}

export function Select({ className = '', ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${CONTROL} ${FOCUS} ${className}`} />;
}

/** Status carries a word and a mark — never colour on its own. */
export function Note({
  tone,
  children,
  role,
}: {
  tone: 'warn' | 'info' | 'good';
  children: ReactNode;
  role?: 'alert' | 'status';
}) {
  const tones = {
    warn: 'border-clay/30 bg-clay-soft text-clay',
    info: 'border-border bg-surface-sunk text-ink-soft',
    good: 'border-sage/30 bg-sage-soft text-sage',
  } as const;
  const mark = { warn: '⚠', info: 'ⓘ', good: '✓' } as const;

  return (
    <p
      role={role}
      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm leading-relaxed ${tones[tone]}`}
    >
      <span aria-hidden="true">{mark[tone]}</span>
      <span>{children}</span>
    </p>
  );
}

export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-surface-sunk ${className}`} />;
}

// ---------------------------------------------------------------- icons

export function BookIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2.5 2.5 0 0 1 2 1 2.5 2.5 0 0 1 2-1h4.5A1.5 1.5 0 0 1 20 5.5v12a1.5 1.5 0 0 1-1.5 1.5H14a2.5 2.5 0 0 0-2 1 2.5 2.5 0 0 0-2-1H5.5A1.5 1.5 0 0 1 4 17.5Z" />
      <path d="M12 5v14" />
    </svg>
  );
}

export function ArrowIcon({ dir, className = 'h-4 w-4' }: { dir: 'up' | 'down'; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {dir === 'up' ? <path d="M12 19V5M6 11l6-6 6 6" /> : <path d="M12 5v14M6 13l6 6 6-6" />}
    </svg>
  );
}

// ---------------------------------------------------------------- loading

export interface Async<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
  set: (value: T) => void;
}

/**
 * One read, with its own loading / error state. `load` is held in a ref so an inline arrow
 * doesn't re-run it forever; `enabled` keeps kids off the adult-only endpoints (the Worker
 * would 403 them anyway — this just avoids the pointless request).
 */
export function useAsync<T>(key: string, load: () => Promise<T>, enabled = true): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(enabled);
  const [nonce, setNonce] = useState(0);

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    loadRef.current().then(
      (result) => {
        if (cancelled) return;
        setData(result);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [key, enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback((value: T) => setData(value), []);

  return { data, error, loading, reload, set };
}
